import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { colors } from "./constants/colors";
import "./ColoredBallsBackground.css";

// Simple debounce function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const ColoredBallsBackground = ({ className = "", isVisible = false }) => {
  const NUMBER_OF_BALLS = window.innerWidth >= 1400 ? 8 : 3;
  const canvasRef = useRef(null);
  const backgroundRef = useRef(null);
  const animationRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const fadeOutStartTimeRef = useRef(null);
  const FADE_OUT_DURATION = 800; // 800ms fade out duration to match CSS

  // Initialize with the provided visibility state
  const [isAnimationActive, setIsAnimationActive] = useState(isVisible);
  const [isBackgroundVisible, setIsBackgroundVisible] = useState(isVisible);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Keep track of last animation state to avoid flickering
  const lastAnimationStateRef = useRef(isVisible);

  // Update state when isVisible changes
  useEffect(() => {
    if (isVisible) {
      // Immediately activate for fade in
      setIsFadingOut(false);
      setIsAnimationActive(true);
      setIsBackgroundVisible(true);
      lastAnimationStateRef.current = true;
    } else {
      // For fade out, keep animation active but start fading out
      setIsFadingOut(true);
      fadeOutStartTimeRef.current = Date.now();

      // After fade duration, actually stop the animation
      setTimeout(() => {
        setIsAnimationActive(false);
        setIsBackgroundVisible(false);
        lastAnimationStateRef.current = false;
      }, FADE_OUT_DURATION);
    }
  }, [isVisible]);

  const getRandomColor = useCallback(() => {
    // Use bright, vibrant colors with high saturation for better visibility
    const primaryColors = [
      colors.primary.pink,
      colors.primary.purple,
      colors.primary.blue,
      colors.primary.lightBlue,
      colors.primary.teal,
    ];
    return primaryColors[Math.floor(Math.random() * primaryColors.length)];
  }, []);

  const getRandomScale = () => {
    const startScale = window.innerWidth >= 1400 ? 15 : 7.5;
    const maxScale =
      window.innerWidth >= 1400
        ? Math.random() * (5 - 2) + 1
        : Math.random() * (3 - 1) + 1;
    // Slightly reduced blur for potential performance gain
    const blur =
      window.innerWidth >= 1400
        ? (Math.random() * (120 - 60) + 60).toFixed(1) // Max 120, min 60
        : (Math.random() * (60 - 20) + 20).toFixed(1); // Max 60, min 20
    return { startScale, maxScale, blur }; // Added blur here
  };

  // Divide the screen into grid cells to ensure spread out starting positions
  const getSpreadOutPositions = (count) => {
    // Calculate grid dimensions (trying to get as close to a square as possible)
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);

    // Create positions array
    const positions = [];

    // Track which cells are already used
    const usedCells = new Set();

    for (let i = 0; i < count; i++) {
      let cellRow, cellCol, cellIndex;

      // Find an unused cell
      do {
        cellRow = Math.floor(Math.random() * rows);
        cellCol = Math.floor(Math.random() * cols);
        cellIndex = cellRow * cols + cellCol;
      } while (usedCells.has(cellIndex) && usedCells.size < rows * cols);

      // Mark cell as used
      usedCells.add(cellIndex);

      // Calculate cell boundaries
      const cellWidth = 1 / cols;
      const cellHeight = 1 / rows;

      // Add some padding to keep balls away from edges
      const padding = 0.1;

      // Calculate random position within the cell
      const x =
        cellCol * cellWidth +
        padding * cellWidth +
        Math.random() * (1 - padding * 2) * cellWidth;
      const y =
        cellRow * cellHeight +
        padding * cellHeight +
        Math.random() * (1 - padding * 2) * cellHeight;

      positions.push({ x, y });
    }

    return positions;
  };

  // Create balls data once
  const balls = useMemo(() => {
    // Get spread out starting positions
    const positions = getSpreadOutPositions(NUMBER_OF_BALLS);

    return Array.from({ length: NUMBER_OF_BALLS }, (_, index) => {
      const { startScale, maxScale, blur } = getRandomScale();
      const position = positions[index];

      // Movement parameters
      const movementSpeed = Math.random() * 0.00003 + 0.00002; // Speed of movement

      // Different movement patterns for each ball
      const pattern = Math.floor(Math.random() * 3); // 0, 1, or 2

      return {
        color1: getRandomColor(),
        color2: getRandomColor(),
        x: position.x, // Current position
        y: position.y,
        initialX: position.x, // Store initial position for movement patterns
        initialY: position.y,
        blur,
        rotation: 0,
        duration: Math.random() * 4 + 20, // Duration in seconds for rotation/scaling
        scales: { startScale, maxScale },
        startTime: Math.random() * 1000, // Random start time offset

        // Movement parameters
        movementSpeed,
        movementPhaseX: Math.random() * Math.PI * 2,
        movementPhaseY: Math.random() * Math.PI * 2,
        pattern, // Different movement patterns

        // Random frequencies for more varied paths
        freqX1: Math.random() * 0.3 + 0.1,
        freqY1: Math.random() * 0.3 + 0.1,
        freqX2: Math.random() * 0.5 + 0.2,
        freqY2: Math.random() * 0.5 + 0.2,

        // Movement range - how far from initial position
        rangeX: Math.random() * 0.2 + 0.1, // 10-30% of screen width
        rangeY: Math.random() * 0.2 + 0.1, // 10-30% of screen height
      };
    });
  }, [NUMBER_OF_BALLS, getRandomColor]);

  // Animation loop with improved smoothness
  const animate = useCallback(() => {
    // Check if animation should be active based on isAnimationActive state
    if (!isAnimationActive && !lastAnimationStateRef.current) {
      // If neither is active, don't animate
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const drawBall = (ctx, ball, elapsedTime) => {
      const width = ctx.canvas.width;
      const height = ctx.canvas.height;

      // Calculate animation progress (0 to 1)
      const cycleTime = ball.duration * 1000; // Convert to milliseconds
      const adjustedTime = (elapsedTime + ball.startTime) % cycleTime;
      const progress = adjustedTime / cycleTime;

      // Calculate current scale and rotation
      const scaleRange = ball.scales.maxScale - ball.scales.startScale;
      const currentScale =
        ball.scales.startScale +
        Math.sin(progress * Math.PI * 2) * (scaleRange / 2);
      const rotation = progress * Math.PI * 2; // 0 to 2π

      // Calculate fade-out effects if we're in fade-out mode
      let opacity = 1.0;
      let scaleMultiplier = 1.0;
      let spreadDistance = 0;

      if (isFadingOut && fadeOutStartTimeRef.current) {
        const fadeElapsed = Date.now() - fadeOutStartTimeRef.current;
        const fadeProgress = Math.min(fadeElapsed / FADE_OUT_DURATION, 1);

        // Decrease opacity as fade progresses
        opacity = 1.0 - fadeProgress;

        // Gradually increase scale during fade-out for a nice effect
        scaleMultiplier = 1.0 + fadeProgress * 0.3; // Scale up to 30% larger

        // Add a slight spread effect - calculate direction from center
        const centerX = 0.5;
        const centerY = 0.5;
        const dirX = ball.initialX - centerX;
        const dirY = ball.initialY - centerY;

        // Normalize direction
        const length = Math.sqrt(dirX * dirX + dirY * dirY) || 0.001;
        const normalizedDirX = dirX / length;
        const normalizedDirY = dirY / length;

        // Apply spread distance based on fade progress
        spreadDistance = fadeProgress * 0.05; // Move up to 5% of screen size
        ball.x = ball.x + normalizedDirX * spreadDistance;
        ball.y = ball.y + normalizedDirY * spreadDistance;
      }

      // Calculate position in pixels
      const x = ball.x * width;
      const y = ball.y * height;

      // Save context state
      ctx.save();

      // Translate to ball center, rotate, then translate back
      ctx.translate(x, y);
      ctx.rotate(rotation);

      // Create gradient
      const gradient = ctx.createRadialGradient(
        0,
        0,
        0,
        0,
        0,
        100 * currentScale * scaleMultiplier
      );
      gradient.addColorStop(0, ball.color1);
      gradient.addColorStop(1, ball.color2);

      // Apply blur and blend mode
      ctx.filter = `blur(${ball.blur * 0.7}px)`; // Reduce blur by 30% for better visibility
      ctx.globalAlpha = opacity; // Apply fade-out effect
      ctx.globalCompositeOperation = "lighten";

      // Draw the ball
      ctx.beginPath();
      ctx.arc(0, 0, 100 * currentScale * scaleMultiplier, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      // Restore context state
      ctx.restore();
    };

    const updateBallPositions = (elapsedTime) => {
      balls.forEach((ball) => {
        const t = elapsedTime * ball.movementSpeed;

        // Different movement patterns based on the ball's pattern value
        switch (ball.pattern) {
          case 0: // Lissajous pattern centered at initial position
            ball.x =
              ball.initialX +
              ball.rangeX * Math.sin(t * ball.freqX1 + ball.movementPhaseX);
            ball.y =
              ball.initialY +
              ball.rangeY * Math.sin(t * ball.freqY1 + ball.movementPhaseY);
            break;

          case 1: // Figure-8 pattern centered at initial position
            ball.x = ball.initialX + ball.rangeX * Math.sin(t * ball.freqX1);
            ball.y =
              ball.initialY + (ball.rangeY * Math.sin(t * ball.freqY1 * 2)) / 2;
            break;

          case 2: // Complex pattern centered at initial position
            ball.x =
              ball.initialX +
              ball.rangeX *
                Math.sin(t * ball.freqX1) *
                Math.cos(t * ball.freqX2 * 0.3);
            ball.y =
              ball.initialY +
              ball.rangeY *
                Math.sin(t * ball.freqY1) *
                Math.sin(t * ball.freqY2 * 0.3);
            break;

          default: // Fallback to simple circular motion
            ball.x = ball.initialX + ball.rangeX * Math.cos(t);
            ball.y = ball.initialY + ball.rangeY * Math.sin(t);
        }

        // Keep balls within bounds with some padding
        ball.x = Math.max(0.05, Math.min(0.95, ball.x));
        ball.y = Math.max(0.05, Math.min(0.95, ball.y));
      });
    };

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    const width = canvas.width;
    const height = canvas.height;

    // Calculate elapsed time since start
    const elapsedTime = Date.now() - startTimeRef.current;

    // Update ball positions
    updateBallPositions(elapsedTime);

    // Clear canvas completely
    ctx.clearRect(0, 0, width, height);

    // Draw each ball with opacity based on animation state
    balls.forEach((ball) => {
      drawBall(ctx, ball, elapsedTime);
    });

    // Continue animation loop
    animationRef.current = requestAnimationFrame(animate);
  }, [balls, isAnimationActive, isFadingOut]);

  // Effect to control animation based on props and manage canvas size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const performResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      // If animation is active, immediately redraw on resize to prevent blank canvas
      if (isAnimationActive || lastAnimationStateRef.current) {
        startTimeRef.current = Date.now(); // Reset start time for smooth visual on resize
      }
    };

    const debouncedResizeCanvas = debounce(performResize, 250); // Debounce with 250ms delay

    performResize(); // Initial resize
    window.addEventListener("resize", debouncedResizeCanvas);

    // Store a reference to the background element
    const backgroundElement = backgroundRef.current;

    return () => {
      window.removeEventListener("resize", debouncedResizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isAnimationActive]);

  // Effect to start/stop animation based on visibility
  useEffect(() => {
    // Start animation if it should be active with a small delay
    if (isAnimationActive) {
      // Always update the last known state
      lastAnimationStateRef.current = true;

      // Add a small delay to ensure the DOM has had time to update visibility classes
      setTimeout(() => {
        // If we already have an animation frame, don't start a new one
        if (!animationRef.current) {
          startTimeRef.current = Date.now();
          animationRef.current = requestAnimationFrame(animate);
        }
      }, 50);
    } else {
      // Cancel any ongoing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }

      // No delay for hiding - immediate transition
      lastAnimationStateRef.current = false;
    }

    // Cleanup animation on unmount
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate, isAnimationActive]);

  // Force initialization and start animation on mount if isVisible is true
  useEffect(() => {
    // Always try to initialize on mount with a slight delay
    setTimeout(() => {
      if (isVisible) {
        startTimeRef.current = Date.now();
        setIsAnimationActive(true);
        setIsBackgroundVisible(true);
        lastAnimationStateRef.current = true;

        // Start animation immediately
        if (!animationRef.current) {
          animationRef.current = requestAnimationFrame(animate);
        }
      }
    }, 100);

    // Cleanup on unmount
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isVisible, animate]);

  // Add proper classes based on visibility
  const bgClassName = `background ${className} ${
    isVisible ? "visible" : "hidden"
  }`;

  return (
    <div className={bgClassName} ref={backgroundRef}>
      <canvas
        ref={canvasRef}
        className="background-canvas"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          willChange: "transform, opacity",
        }}
      />
    </div>
  );
};

export default ColoredBallsBackground;
